import React, { useState, useEffect } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaCreditCard,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle
} from "react-icons/fa";

function PaymentMethodsManager() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [deletingMethod, setDeletingMethod] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingMethod, setPermanentDeletingMethod] = useState(null);
  const [formData, setFormData] = useState({
    payment_description: '',
    active: 1
  });

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await getData('/api/admin/get_payment_methods');
      setPaymentMethods(response.data.paymentMethods || []);
    } catch (error) {
      console.error('Error cargando métodos de pago:', error);
      toastError('Error al cargar los métodos de pago');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          payment_description: formData.payment_description.trim(),
          active: formData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingMethod) {
        dataToSend.data.id = editingMethod.id;
        await putData('/api/admin/update_payment_method', dataToSend);
        toastSuccess(`Método de pago "${formData.payment_description}" actualizado exitosamente`);
      } else {
        await postData('/api/admin/create_payment_method', dataToSend);
        toastSuccess(`Método de pago "${formData.payment_description}" creado exitosamente`);
      }
      
      setShowModal(false);
      resetForm();
      await loadPaymentMethods();
    } catch (error) {
      console.error('Error guardando método de pago:', error);
      toastError(editingMethod ? 'Error al actualizar el método de pago' : 'Error al crear el método de pago');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingMethod(null);
    setFormData({
      payment_description: '',
      active: 1
    });
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData({
      payment_description: method.payment_description || '',
      active: method.active !== undefined ? method.active : 1
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingMethod(null);
    setFormData({
      payment_description: '',
      active: 1
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (method) => {
    setDeletingMethod(method);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingMethod) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo (soft delete)
      const dataToSend = {
        data: {
          id: deletingMethod.id,
          payment_description: deletingMethod.payment_description,
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_payment_method', dataToSend);
      toastSuccess(`Método de pago "${deletingMethod.payment_description}" eliminado exitosamente`);
      setShowDeleteModal(false);
      setDeletingMethod(null);
      await loadPaymentMethods();
    } catch (error) {
      console.error('Error eliminando método de pago:', error);
      toastError('Error al eliminar el método de pago');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingMethod(null);
  };

  // Funciones de eliminación PERMANENTE
  const handlePermanentDelete = (method) => {
    setPermanentDeletingMethod(method);
    setShowPermanentDeleteModal(true);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletingMethod) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingMethod.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_payment_method', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setPaymentMethods(prevMethods => prevMethods.filter(method => method.id !== permanentDeletingMethod.id));
      
      toastSuccess(`Método de pago "${permanentDeletingMethod.payment_description}" eliminado permanentemente`);
      setShowPermanentDeleteModal(false);
      setPermanentDeletingMethod(null);
    } catch (error) {
      console.error('Error eliminando método de pago:', error);
      toastError(error.message || 'Error al eliminar el método de pago definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteModal(false);
    setPermanentDeletingMethod(null);
  };

  // Filtrar métodos de pago según el toggle
  const filteredPaymentMethods = showInactive 
    ? paymentMethods 
    : paymentMethods.filter(method => method.active);

  return (
    <div className="payment-methods-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaCreditCard className="me-2 text-primary" />
            Gestión de Métodos de Pago
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nuevo Método de Pago
            </button>
          </div>
        </div>

        <div className="card-body">
          {!showInactive && paymentMethods.length > filteredPaymentMethods.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{paymentMethods.length - filteredPaymentMethods.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando métodos de pago...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Descripción del Método</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Última Actualización</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPaymentMethods.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        {showInactive ? 'No hay métodos de pago registrados' : 'No hay métodos de pago activos. Activa el toggle "Mostrar inactivos" para ver métodos eliminados.'}
                      </td>
                    </tr>
                  ) : (
                    filteredPaymentMethods.map(method => (
                      <tr key={method.id}>
                        <td>#{method.id}</td>
                        <td>
                          <strong>{method.payment_description}</strong>
                        </td>
                        <td>
                          <span className={`badge ${method.active ? 'bg-success' : 'bg-danger'}`}>
                            {method.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {method.inserted_date ? new Date(method.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {method.updated_at ? new Date(method.updated_at).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(method)}
                              disabled={loading}
                              title="Editar método de pago"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => handleDelete(method)}
                              disabled={loading || !method.active}
                              title={method.active ? "Inactivar método" : "Método ya inactivo"}
                            >
                              <FaTrash />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handlePermanentDelete(method)}
                              disabled={loading}
                              title="Eliminar definitivamente (irreversible)"
                            >
                              <FaExclamationTriangle />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar método de pago */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingMethod ? 'Editar Método de Pago' : 'Nuevo Método de Pago'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Descripción del Método de Pago *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="payment_description"
                      value={formData.payment_description}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      placeholder="Ej: Mercado Pago, Tarjeta de Crédito, Efectivo, etc."
                    />
                    <div className="form-text">
                      Ingresa una descripción clara del método de pago
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={formData.active === 1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0
                        }))}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Método de pago activo
                      </label>
                      <div className="form-text">
                        Los métodos inactivos no aparecerán disponibles para los clientes
                      </div>
                    </div>
                  </div>

                  {/* Ejemplos de métodos de pago comunes */}
                  <div className="mb-3">
                    <label className="form-label">Ejemplos comunes:</label>
                    <div className="d-flex flex-wrap gap-1">
                      {['Mercado Pago', 'Tarjeta de Crédito', 'Tarjeta de Débito', 'Efectivo', 'Transferencia Bancaria', 'PayPal'].map(example => (
                        <button
                          key={example}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setFormData(prev => ({ ...prev, payment_description: example }))}
                          disabled={loading}
                        >
                          {example}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.payment_description.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingMethod ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el método de pago <strong>"{deletingMethod?.payment_description}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El método de pago será marcado como inactivo y no aparecerá disponible para los clientes.
                </div>
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  Los pedidos existentes que usen este método de pago no se verán afectados.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE */}
      {showPermanentDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente método de pago?</p>
                {permanentDeletingMethod && (
                  <div className="alert alert-warning">
                    <strong>Método de Pago:</strong> {permanentDeletingMethod.payment_description}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El método de pago se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                  <li>Los pedidos que usen este método no podrán mostrar el nombre del método</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este método,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentMethodsManager;
